import { useCallback, useEffect, useRef, useState } from 'react';
import { zaehleNeu } from '../services/talentpool/talentpoolService';

/**
 * Zahl der neuen Bewerbungen (Status `neu`) für das Abzeichen am Menüpunkt
 * „Talentpool“ (docs/talentpool-plan.md). Takt wie `useMaterialOffen`:
 *   * beim Start (sobald `aktiv` wird),
 *   * alle fünf Minuten, solange das Fenster sichtbar ist,
 *   * wenn das Fenster den Fokus bekommt oder wieder sichtbar wird —
 *     höchstens alle 30 s, denn Fokus und Sichtbarkeit kommen oft zusammen.
 * Dazu meldet der Talentpool selbst (`aktualisiere`), sobald er geladen oder
 * einen Status geändert hat; eine ältere Antwort, die danach eintrifft,
 * überschreibt das nicht.
 *
 * `aktiv` heißt: der Menüpunkt ist sichtbar (Recht `talentpool.verwalten`,
 * nicht in der Handy-App). Sonst wird nichts gefragt, und das Abzeichen
 * steht auf 0. Fail-open: `null` (Fehler, Tabelle fehlt) hält den letzten Wert.
 */

/** Takt für das Nachzählen. */
export const TALENTPOOL_TAKT_MS = 5 * 60 * 1000;
/** Mindestabstand zwischen zwei Ladungen aus Fokus oder Sichtbarkeit. */
export const TALENTPOOL_FOKUS_ABSTAND_MS = 30 * 1000;

export type TalentpoolLadeAnlass = 'start' | 'takt' | 'fokus';

/**
 * Soll jetzt gezählt werden? Der Start immer; Takt und Fokus nur bei
 * sichtbarem Fenster (ein Tab im Hintergrund kostet sonst alle fünf Minuten
 * eine Anfrage); Fokus zusätzlich mit Abstand.
 */
export const sollTalentpoolLaden = (a: {
    anlass: TalentpoolLadeAnlass;
    sichtbar: boolean;
    jetzt: number;
    /** Beginn der letzten Ladung; null = noch keine. */
    zuletzt: number | null;
}): boolean => {
    if (a.anlass === 'start') return true;
    if (!a.sichtbar) return false;
    if (a.anlass === 'takt' || a.zuletzt === null) return true;
    return a.jetzt - a.zuletzt >= TALENTPOOL_FOKUS_ABSTAND_MS;
};

/**
 * Neuer Stand des Abzeichens aus einer Antwort. Unbekannt (`null`) oder
 * keine Zahl: der alte Wert bleibt. Sonst ganzzahlig und nie negativ.
 */
export const talentpoolAnzahlAusAntwort = (bisher: number, antwort: number | null | undefined): number => {
    if (typeof antwort !== 'number' || !Number.isFinite(antwort)) return bisher;
    return Math.max(0, Math.trunc(antwort));
};

const fensterSichtbar = (): boolean =>
    typeof document === 'undefined' || document.visibilityState !== 'hidden';

export interface TalentpoolNeu {
    anzahl: number;
    /** Der Talentpool hat neu gezählt — diese Zahl gilt ab sofort. */
    aktualisiere: (n: number) => void;
}

export function useTalentpoolNeu(aktiv: boolean): TalentpoolNeu {
    const [anzahl, setAnzahl] = useState(0);
    // Jede Ladung und jede Meldung aus der Ansicht zählt hoch; eine Antwort,
    // deren Anfrage vor einer neueren begann, gilt nicht mehr.
    const folgeRef = useRef(0);
    const zuletztRef = useRef<number | null>(null);

    useEffect(() => {
        if (!aktiv) return;
        let lebt = true;
        const lade = (anlass: TalentpoolLadeAnlass) => {
            const jetzt = Date.now();
            if (!sollTalentpoolLaden({ anlass, sichtbar: fensterSichtbar(), jetzt, zuletzt: zuletztRef.current })) return;
            zuletztRef.current = jetzt;
            const folge = ++folgeRef.current;
            zaehleNeu()
                .then((n) => {
                    if (lebt && folge === folgeRef.current) setAnzahl((bisher) => talentpoolAnzahlAusAntwort(bisher, n));
                })
                .catch(() => undefined);
        };
        lade('start');
        const takt = window.setInterval(() => lade('takt'), TALENTPOOL_TAKT_MS);
        const beiFokus = () => lade('fokus');
        const beiSichtbarkeit = () => { if (fensterSichtbar()) lade('fokus'); };
        window.addEventListener('focus', beiFokus);
        document.addEventListener('visibilitychange', beiSichtbarkeit);
        return () => {
            lebt = false;
            window.clearInterval(takt);
            window.removeEventListener('focus', beiFokus);
            document.removeEventListener('visibilitychange', beiSichtbarkeit);
            // Recht weg oder abgemeldet: der Nächste erbt die Zahl nicht.
            // Eine Antwort, die noch unterwegs ist, hält `lebt` auf.
            zuletztRef.current = null;
            setAnzahl(0);
        };
    }, [aktiv]);

    const aktualisiere = useCallback((n: number) => {
        folgeRef.current++;
        zuletztRef.current = Date.now();
        setAnzahl((bisher) => talentpoolAnzahlAusAntwort(bisher, n));
    }, []);

    return { anzahl: aktiv ? anzahl : 0, aktualisiere };
}
